import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Bell, BellRing, Search, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { Reveal } from "../components/Chrome";

export function deviceId() {
  let id = localStorage.getItem("klyx_device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("klyx_device_id", id);
  }
  return id;
}

export function Stars({ value = 0, size = 14, onSelect, testPrefix = "stars" }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testPrefix}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testPrefix}-${n}`}
          disabled={!onSelect}
          onClick={() => onSelect && onSelect(n)}
          className={onSelect ? "cursor-pointer hover:scale-110 transition-transform duration-150" : "cursor-default"}
        >
          <Star size={size} className={n <= Math.round(value) ? "text-mauve-light fill-mauve" : "text-zinc-400"} />
        </button>
      ))}
    </div>
  );
}

export function ReviewPanel({ app }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [name, setName] = useState(() => localStorage.getItem("klyx_name") || "");
  const [comment, setComment] = useState("");
  const { data } = useQuery({
    queryKey: ["app-reviews", app.id],
    queryFn: async () => (await api.get(`/apps/${app.id}/reviews`, { params: { device_id: deviceId() } })).data,
  });

  useEffect(() => {
    api.post(`/apps/${app.id}/view`).catch(() => {});
  }, [app.id]);

  useEffect(() => {
    if (data?.mine && rating === 0) setRating(data.mine.rating);
  }, [data, rating]);

  const submit = useMutation({
    mutationFn: async () =>
      (await api.post(`/apps/${app.id}/reviews`, { device_id: deviceId(), name: name.trim(), rating, comment: comment.trim() })).data,
    onSuccess: (res) => {
      if (name.trim()) localStorage.setItem("klyx_name", name.trim());
      setComment("");
      qc.invalidateQueries({ queryKey: ["app-reviews", app.id] });
      qc.invalidateQueries({ queryKey: ["apps"] });
      qc.invalidateQueries({ queryKey: ["app", app.id] });
      toast.success(res.pending_approval ? "Thanks! Your comment is awaiting approval." : "Rating saved. Thank you!");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="review-panel">
      <div className="border border-white/10 bg-elevated p-5">
        <p className="text-xs text-zinc-200 mb-3">Your rating {data?.mine && <span className="text-zinc-300">(you rated this before — submitting updates it)</span>}</p>
        <Stars value={rating} size={22} onSelect={setRating} testPrefix="review-rating-star" />
        <input
          data-testid="review-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="mt-4 w-full bg-ink border border-white/15 px-4 py-2.5 text-sm text-silver placeholder:text-zinc-400 focus:border-mauve focus:outline-none"
        />
        <textarea
          data-testid="review-comment-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Short feedback (optional — shown after admin approval)"
          className="mt-3 w-full bg-ink border border-white/15 px-4 py-2.5 text-sm text-silver placeholder:text-zinc-400 focus:border-mauve focus:outline-none resize-none"
        />
        <button
          data-testid="review-submit-button"
          disabled={rating === 0 || submit.isPending}
          onClick={() => submit.mutate()}
          className="mt-4 bg-mauve text-white px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
        >
          {submit.isPending ? "Sending…" : "Submit"}
        </button>
      </div>

      <div className="mt-8">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-4">
          Community feedback {data ? `(${data.reviews.length})` : ""}
        </p>
        {data?.reviews?.length === 0 && (
          <p data-testid="reviews-empty" className="text-sm text-zinc-300 border border-dashed border-white/15 p-6 text-center">
            No approved comments yet. Be the first.
          </p>
        )}
        <div className="space-y-4">
          {data?.reviews?.map((r) => (
            <div key={r.id} data-testid={`review-item-${r.id}`} className="border border-white/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-silver">{r.name}</p>
                <Stars value={r.rating} size={12} testPrefix={`review-stars-${r.id}`} />
              </div>
              <p className="mt-2 text-sm text-zinc-200 leading-relaxed">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NotifyMe({ app, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(() => localStorage.getItem(`klyx_notify_${app.id}`) === "1");
  const submit = useMutation({
    mutationFn: async () => (await api.post(`/apps/${app.id}/notify`, { email: email.trim() })).data,
    onSuccess: () => {
      localStorage.setItem(`klyx_notify_${app.id}`, "1");
      setDone(true);
      setOpen(false);
      toast.success("You're on the list — we'll email you at launch");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const pad = size === "lg" ? "px-8 py-4 text-xs" : "px-5 py-2.5 text-[11px]";

  if (done)
    return (
      <span data-testid={`notify-done-${app.id}`} className={`inline-flex items-center gap-2 border border-white/15 text-zinc-200 ${pad} tracking-[0.2em] uppercase font-medium`}>
        <BellRing size={14} className="text-mauve-light" /> On the list
      </span>
    );
  if (!open)
    return (
      <button
        type="button"
        data-testid={`notify-me-open-${app.id}`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 border border-white/15 text-zinc-300 ${pad} tracking-[0.2em] uppercase font-medium hover:border-mauve hover:text-white transition-colors duration-200`}
      >
        <Bell size={14} /> Notify me
      </button>
    );
  return (
    <form
      data-testid={`notify-form-${app.id}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim()) submit.mutate();
      }}
      className="flex gap-2"
    >
      <input
        type="email"
        autoFocus
        data-testid={`notify-email-input-${app.id}`}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        className="w-44 bg-ink border border-white/15 px-3 py-2 text-sm text-silver placeholder:text-zinc-400 focus:border-mauve focus:outline-none"
      />
      <button
        type="submit"
        data-testid={`notify-submit-${app.id}`}
        disabled={submit.isPending || !email.trim()}
        className="bg-mauve text-white px-4 text-[11px] tracking-[0.15em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
      >
        {submit.isPending ? "…" : "Join"}
      </button>
    </form>
  );
}

function ReviewsModal({ app, onClose }) {
  return (
    <div data-testid="app-reviews-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-xl max-h-[85vh] overflow-y-auto border border-white/15 bg-surface p-8"
      >
        <button data-testid="app-reviews-close" onClick={onClose} className="absolute top-4 right-4 text-zinc-300 hover:text-white transition-colors duration-200">
          <X size={18} />
        </button>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve-light mb-2">Rate &amp; review</p>
        <h3 className="font-heading font-black text-2xl text-silver mb-6">{app.name}</h3>
        <ReviewPanel app={app} />
      </motion.div>
    </div>
  );
}

export function launchApp(app) {
  api.post(`/apps/${app.id}/launch`).catch(() => {});
  window.open(app.url, "_blank", "noopener,noreferrer");
}

function AppCard({ app, index, onOpenReviews }) {
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div data-testid={`app-card-${app.id}`} className="group flex flex-col h-full border border-white/10 bg-elevated transition-[border-color,transform] duration-300 hover:border-mauve/50 hover:-translate-y-1">
        <Link to={`/apps/${app.id}`} data-testid={`app-detail-link-${app.id}`} className="block aspect-[16/10] overflow-hidden bg-surface">
          {app.image ? (
            <img src={app.image} alt={app.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-heading font-black text-4xl text-white/10">{app.name?.[0]}</div>
          )}
        </Link>
        <div className="p-6 lg:p-7 flex flex-col flex-1">
          <div className="flex items-center justify-between gap-3">
            <Link to={`/apps/${app.id}`} className="font-heading font-black text-xl sm:text-2xl tracking-tight text-silver hover:text-mauve-light transition-colors duration-200">
              {app.name}
            </Link>
            <div className="flex items-center gap-2">
              <Stars value={app.avg_rating} testPrefix={`app-stars-${app.id}`} />
              <span className="text-xs text-zinc-300">({app.rating_count})</span>
            </div>
          </div>
          <p className="mt-3 text-sm text-zinc-200 leading-relaxed flex-1">{app.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {app.tags?.map((t) => (
              <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-mauve-light border border-mauve/40 px-2 py-0.5">
                {t}
              </span>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {app.coming_soon ? (
              <>
                <span data-testid={`app-coming-soon-${app.id}`} className="inline-flex items-center gap-2 border border-mauve/40 bg-mauve/10 text-mauve-light px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold">
                  Coming soon
                </span>
                <NotifyMe app={app} />
              </>
            ) : (
              <button
                data-testid={`app-launch-${app.id}`}
                onClick={() => launchApp(app)}
                className="inline-flex items-center gap-2 bg-mauve text-white px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
              >
                Launch <ArrowUpRight size={14} />
              </button>
            )}
            <button
              data-testid={`app-reviews-open-${app.id}`}
              onClick={() => onOpenReviews(app)}
              className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-medium hover:border-mauve hover:text-white transition-colors duration-200"
            >
              Rate &amp; review
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function AppsShowcase() {
  usePageMeta("Apps — Klyx™", "Installable, offline-capable progressive web apps built and shipped by Klyx™.");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [openApp, setOpenApp] = useState(null);

  const { data: apps = [] } = useQuery({ queryKey: ["apps"], queryFn: async () => (await api.get("/apps")).data });
  const { data: catData } = useQuery({ queryKey: ["app-categories"], queryFn: async () => (await api.get("/apps/categories")).data });
  const categories = ["All", ...(catData?.categories || [])];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return apps.filter((a) => {
      if (cat !== "All" && !(a.tags || []).includes(cat)) return false;
      if (!needle) return true;
      return a.name.toLowerCase().includes(needle) || (a.description || "").toLowerCase().includes(needle);
    });
  }, [apps, q, cat]);

  return (
    <div data-testid="apps-page" className="min-h-screen">
      <section className="pt-40 pb-16 lg:pt-52 spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[2px] w-10 bg-mauve shrink-0" />
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light">The app family</p>
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-silver">
              Apps, built and shipped by Klyx™.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-200 max-w-2xl leading-relaxed">
              Installable, offline-capable progressive web apps. Launch any of them free in your browser — and tell us what you think.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="relative w-full lg:max-w-sm">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-300" />
                <input
                  data-testid="apps-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search apps by name or description…"
                  className="w-full bg-surface border border-white/15 pl-11 pr-4 py-3 text-sm text-silver placeholder:text-zinc-400 focus:border-mauve focus:outline-none"
                />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="apps-category-filters">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`apps-category-filter-${c.toLowerCase()}`}
                    onClick={() => setCat(c)}
                    className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-medium border transition-colors duration-200 ${
                      cat === c ? "border-mauve bg-mauve/15 text-mauve-light" : "border-white/15 text-zinc-200 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {filtered.length === 0 ? (
            <p data-testid="apps-empty" className="text-zinc-300 text-sm border border-dashed border-white/15 p-16 text-center">
              No apps match your search.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {filtered.map((a, i) => (
                <AppCard key={a.id} app={a} index={i} onOpenReviews={setOpenApp} />
              ))}
            </div>
          )}
        </div>
      </section>

      {openApp && <ReviewsModal app={openApp} onClose={() => setOpenApp(null)} />}
    </div>
  );
}
